/**
 * Booking Service
 *
 * POST /api/public/bookings (contract §3.4). From v1.3 this creates a
 * booking request: the backend answers status 'scheduled' (meeting_url
 * null), holds the slot, and the owner confirms or declines it in /admin.
 * The backend is authoritative for price, duration and end time. The client
 * sends the slot's startISO unchanged.
 *
 * Client self-service (cancel/reschedule/lookup) is deferred; the owner
 * manages bookings through the admin API.
 */

import { post } from './apiClient';
import { getServiceSlug } from '../utils/serviceMapper';
import { isIsoWithOffset } from '../utils/dateUtils';

/**
 * Trimmed string, or null for empty/absent (contract §1.5: all mean "not provided").
 * @param {unknown} value
 * @returns {string|null}
 */
const optionalString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

/**
 * Build the snake_case request body.
 *
 * @param {object} form - Checkout form values
 * @param {string} form.firstName
 * @param {string} form.lastName
 * @param {string} form.email
 * @param {string} [form.phone]
 * @param {string} [form.notes]
 * @param {boolean} form.policyAccepted - The policy acknowledgement checkbox
 * @param {string} [form.website] - Honeypot; real visitors leave it empty
 * @param {{startISO: string}} slot - Slot returned by getAvailability
 * @param {string|{slug: string}} service - Service slug (or service object)
 * @returns {object} Request body for POST /api/public/bookings
 */
export const buildBookingPayload = (form, slot, service) => ({
  service_slug: getServiceSlug(service),
  first_name: (form.firstName || '').trim(),
  last_name: (form.lastName || '').trim(),
  email: (form.email || '').trim().toLowerCase(),
  phone: optionalString(form.phone),
  notes: optionalString(form.notes),
  start_time: slot.startISO,
  policy_accepted: form.policyAccepted === true,
  website: typeof form.website === 'string' ? form.website : '',
});

/**
 * Normalize the 201 `data` object.
 * @param {object} data
 * @returns {{bookingReference:string, serviceSlug:string, serviceName:string, startISO:string, endISO:string, amount:number, currency:string, paymentStatus:string, status:string, paymentsEnabled:boolean, meetingUrl:string|null}}
 */
export const normalizeBooking = (data) => ({
  bookingReference: data.booking_reference,
  serviceSlug: data.service_slug,
  serviceName: data.service_name,
  startISO: data.start_time,
  endISO: data.end_time,
  amount: data.amount,
  currency: data.currency,
  paymentStatus: data.payment_status,
  status: data.status,
  paymentsEnabled: data.payments_enabled === true,
  meetingUrl: data.meeting_url ?? null,
});

/**
 * Create a booking.
 *
 * @param {object} form - See buildBookingPayload
 * @param {{startISO: string, endISO?: string}} slot - Slot from getAvailability
 * @param {string|{slug: string}} service - Service slug (or service object)
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ReturnType<typeof normalizeBooking>>}
 * @throws {ApiError} 400 validation_error, 409 slot_unavailable, 422 notice_period,
 *   429 rate_limited, 503 calendar_unavailable (message already user-facing)
 * @throws {Error} Client-side validation failure (message user-facing)
 */
export const createBooking = async (form, slot, service, { signal } = {}) => {
  if (!form) {
    throw new Error('Please fill in your details.');
  }
  const missing = ['firstName', 'lastName', 'email'].filter(
    (field) => typeof form[field] !== 'string' || !form[field].trim()
  );
  if (missing.length) {
    throw new Error(`Missing required fields: ${missing.join(', ')}`);
  }
  if (!slot || !isIsoWithOffset(slot.startISO)) {
    throw new Error('Please select an available time before continuing.');
  }
  if (form.policyAccepted !== true) {
    throw new Error('Please confirm that you agree to the policies before booking.');
  }

  const data = await post('/api/public/bookings', buildBookingPayload(form, slot, service), { signal });
  return normalizeBooking(data);
};
